import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, ShieldAlert } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { requireAdmin } from "@/lib/admin-guard";
import { adminConfirmTestUser } from "@/lib/admin-test-users.functions";

export const Route = createFileRoute("/admin_/confirm-test-user")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Auto-confirm test user — Recovery Trauma™" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ConfirmTestUserPage,
});

type Result =
  | { kind: "ok"; email: string; user_id: string; already_confirmed: boolean }
  | { kind: "error"; message: string };

function ConfirmTestUserPage() {
  const confirmFn = useServerFn(adminConfirmTestUser);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await confirmFn({ data: { email: trimmed } });
      setResult({
        kind: "ok",
        email: res.email,
        user_id: res.user_id,
        already_confirmed: res.already_confirmed,
      });
    } catch (err) {
      setResult({
        kind: "error",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>

      <h1 className="text-2xl font-semibold tracking-tight">Auto-confirm test user</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Marks a Supabase auth user's email as confirmed so E2E runs can skip the inbox
        step. Restricted to the test-email allowlist:{" "}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">janabausg2905@gmail.com</code>,{" "}
        addresses containing <code className="rounded bg-muted px-1 py-0.5 text-xs">+test</code>,{" "}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">+launchtest</code>, or{" "}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">+e2e</code>, and{" "}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">@example.com</code> /{" "}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">@example.test</code> domains.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-3">
        <label htmlFor="email" className="block text-sm font-medium">
          Test email
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jana+launchtest1234@recoverytrauma.com"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {busy ? "Confirming…" : "Auto-confirm"}
        </button>
      </form>

      {result && (
        <div
          className={`mt-6 rounded-lg border p-4 text-sm ${
            result.kind === "ok"
              ? "border-emerald-500/40 bg-emerald-500/10"
              : "border-destructive/40 bg-destructive/10"
          }`}
        >
          {result.kind === "ok" ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-4 w-4" />
                {result.already_confirmed
                  ? "Already confirmed — no change needed"
                  : "Email confirmed successfully"}
              </div>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <dt>Email</dt>
                <dd className="font-mono text-foreground">{result.email}</dd>
                <dt>User ID</dt>
                <dd className="font-mono text-foreground">{result.user_id}</dd>
                <dt>Status</dt>
                <dd className="text-foreground">
                  {result.already_confirmed ? "already_confirmed = true" : "already_confirmed = false"}
                </dd>
              </dl>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
              <div>
                <div className="font-medium text-destructive">Failed</div>
                <div className="mt-1 text-xs text-muted-foreground">{result.message}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          Every call is written to <code>admin_access_audit</code>. Non-test emails are
          refused by the server before the Auth Admin API is touched.
        </div>
      </div>
    </div>
  );
}
